import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Box, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { AppState } from "redux/store";
import PageMappingTable from "./PageMappingTable";
import { getPlangetPlansByGroupCode } from "redux/actions/planMaster";

const PageMapping = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [searchField, setSearchField] = useState("all");

  const { planPageData } = useSelector<AppState, AppState["planMaster"]>(
    ({ planMaster }) => planMaster
  );

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Plan Group Code", value: "planGroupCode" },
    { label: "Plans Count", value: "groupCount" },
  ];

  useEffect(() => {
    dispatch(getPlangetPlansByGroupCode());
  }, [dispatch]);

  const onPageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPage(value);
  };

  const handleSearchFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchField(e.target.value);
    setSearch("");
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const filteredData = search.trim()
    ? planPageData.filter((item) => {
        const searchTerm = search.trim().toLowerCase();
        if (searchField === "all") {
          return searchFields
            .filter((f) => f.value !== "all")
            .some(({ value }) => {
              const fieldValue = item[value];
              return fieldValue?.toString().toLowerCase().includes(searchTerm);
            });
        } else {
          const fieldValue = item[searchField];
          return fieldValue?.toString().toLowerCase().includes(searchTerm);
        }
      })
    : planPageData;

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.support.planPageMapping"]}
            </Box>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              width: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={searchField}
              onChange={handleSearchFieldChange}
              SelectProps={{ native: true }}
            >
              {searchFields.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={messages["common.searchHere"] as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton
                        onClick={() => handleSearchChange("")}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <PageMappingTable pageMapping={paginatedData} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredData.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default PageMapping;
